import * as React from "react";
import { useEffect } from "react";
import { useUserContext } from "../user/use-user-context";
import { useUserProducts } from "../user/use-user-products";
import { ensureCorrectWidget } from "./raffle-ai-helpers";
import { RaffleAiConfig } from "./use-raffle-ai-config";

interface RaffleAiWidgetProps extends RaffleAiConfig {
  productCode?: string;
}

/**
 * Manages Raffle AI widget lifecycle based on user authentication and product context.
 *
 * Behavior:
 * - Widgets are lazy-loaded only when entering a product/document page with a configured widget
 * - Widgets persist across navigation to preserve conversation state
 * - Widget is destroyed when:
 *   - User is not authenticated
 *   - User lacks access to Raffle-enabled products
 *   - Navigating to a product without a configured widget
 * - No cleanup on unmount - lifecycle is managed by ensureCorrectWidget() based on productCode changes
 */
const RaffleAiWidget: React.FC<RaffleAiWidgetProps> = ({
  productWidgetMapping,
  productCode
}) => {
  const { isAuthenticated, status: userStatus } = useUserContext();
  const { products, status: productsStatus } = useUserProducts();

  useEffect(() => {
    // Wait for user and products data to load
    if (userStatus !== "success" || productsStatus !== "success") {
      return;
    }

    // Early exit if user is not authenticated
    if (!isAuthenticated) {
      ensureCorrectWidget(undefined, productWidgetMapping, products);
      return;
    }

    // Delegate all widget lifecycle logic to ensureCorrectWidget
    ensureCorrectWidget(productCode, productWidgetMapping, products);
  }, [
    productCode,
    productWidgetMapping,
    isAuthenticated,
    userStatus,
    products,
    productsStatus
  ]);

  return null;
};

export default RaffleAiWidget;
